import React, { FC, useMemo } from 'react';
import { Trans, useTranslation } from '@blocs.i18n';
import { BodyText, FlexRow, Link } from '@minecraft.atoms';
import { gqlMediaTypeCodeEnum } from '@minecraft.graphql-types';
import { UseCreditsUI } from '@consumer-talent.components/core/useCreditsUI';
import { useRouteHelper } from '@consumer-talent.utils/routeHelper';

const ClaimThemLink: FC<{ to: string; disabled: boolean; totalCount: number }> = ({ to, disabled, totalCount }) => {
  const { t } = useTranslation();

  if (disabled) {
    return <>{t('talent:creditAvailableBanner.claim', { count: totalCount })}</>;
  }

  return <Link to={to}>{t('talent:creditAvailableBanner.claim', { count: totalCount })}</Link>;
};

const CreditType = ({ mediaType, quantity }: { mediaType: gqlMediaTypeCodeEnum; quantity: number }) => {
  const { t } = useTranslation();

  if (mediaType === gqlMediaTypeCodeEnum.PHOTO) {
    return <>{t('talent:creditAvailableBanner.photoCredits', { count: quantity })}</>;
  }
  if (mediaType === gqlMediaTypeCodeEnum.VIDEO) {
    return <>{t('talent:creditAvailableBanner.videoCredits', { count: quantity })}</>;
  }
  if (mediaType === gqlMediaTypeCodeEnum.AUDIO) {
    return <>{t('talent:creditAvailableBanner.audioCredits', { count: quantity })}</>;
  }

  return <></>;
};

export const MediaPersistentBanner = ({
  isMediaRoute,
  priorityMediaToRedeem,
  availableCreditsByType,
}: {
  isMediaRoute?: boolean;
  priorityMediaToRedeem: UseCreditsUI['priorityMediaToRedeem'];
  availableCreditsByType?: UseCreditsUI['availableCreditsByType'];
}) => {
  const { i18n } = useTranslation();
  const { link } = useRouteHelper();

  // This may need to be updated in the future, we didn't have time to get all combinations of languages
  const phrase = useMemo(() => {
    let key = 'talent:creditAvailableBanner.fallback';
    let totalCount = 0;
    const countPhrases = [];
    // We concatenate words based on the available credits by type
    if (availableCreditsByType.PHOTO > 0) {
      countPhrases.push(
        <CreditType
          mediaType={gqlMediaTypeCodeEnum.PHOTO}
          quantity={availableCreditsByType[gqlMediaTypeCodeEnum.PHOTO]}
        />
      );
      totalCount += availableCreditsByType[gqlMediaTypeCodeEnum.PHOTO];
    }
    if (availableCreditsByType.VIDEO > 0) {
      countPhrases.push(
        <CreditType
          mediaType={gqlMediaTypeCodeEnum.VIDEO}
          quantity={availableCreditsByType[gqlMediaTypeCodeEnum.VIDEO]}
        />
      );
      totalCount += availableCreditsByType[gqlMediaTypeCodeEnum.VIDEO];
    }
    if (availableCreditsByType.AUDIO > 0) {
      countPhrases.push(
        <CreditType
          mediaType={gqlMediaTypeCodeEnum.AUDIO}
          quantity={availableCreditsByType[gqlMediaTypeCodeEnum.AUDIO]}
        />
      );
      totalCount += availableCreditsByType[gqlMediaTypeCodeEnum.AUDIO];
    }

    // Based on how many phrases we pushed (based on number of credits)
    // we need to decide which phrase to say (not just simple pluralization)
    if (countPhrases.length === 3) {
      key = 'talent:creditAvailableBanner.threeTypes';
    } else if (countPhrases.length === 2) {
      key = 'talent:creditAvailableBanner.twoTypes';
    } else if (countPhrases.length === 1) {
      key = 'talent:creditAvailableBanner.oneType';
    }

    // Return the phrase for all three media type availabilities
    return (
      <Trans
        i18nKey={key}
        components={[
          <ClaimThemLink
            disabled={isMediaRoute}
            to={link('mediaBank', priorityMediaToRedeem)}
            totalCount={totalCount}
          />,
          ...countPhrases,
        ]}
      />
    );
    // "i18n.language" is needed to refresh this language when the user switches languages:
  }, [availableCreditsByType, isMediaRoute, link, priorityMediaToRedeem, i18n?.language]);

  return (
    <FlexRow
      data-testid="user-credits-warning-banner"
      justifyContent="center"
      w="full"
      bgColor="warning"
      className="cn_atom_p-4"
      gap="1"
    >
      <BodyText fontWeight="medium" lineHeight="expanded" align="center">
        {phrase}
      </BodyText>
    </FlexRow>
  );
};
